import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";

import { Button } from "../../../../../components/Button";
import { DialogFooter } from "../../../../../components/DialogFooter";
import { PageWrapper } from "../../../../../components/PageWrapper";
import { TextButton } from "../../../../../components/TextButton";
import { CreditProgressBarWithTooltip } from "../../../../../EmergencyCredits/components/CreditProgressBarWithTooltip";
import { getCreditProgressBarLabel } from "../../../../../EmergencyCredits/utils/getCreditProgressBarLabel";
import { type YourEmergencyCreditsPageProps } from "./types";

function shouldShowTooltip(
  completedShifts: number,
  requiredShifts: number,
  hasCredit: boolean
): boolean {
  // Show tooltip if: less than full progress AND doesn't have a credit already
  const hasPartialProgress = completedShifts < requiredShifts;
  return hasPartialProgress && !hasCredit;
}

export function YourEmergencyCreditsPage(props: YourEmergencyCreditsPageProps) {
  const { backgroundGradient, cancellationStats, latenessStats, onContinue, onLearnMoreClick } =
    props;

  const hasCancellationCredit = cancellationStats.totalAvailableCredits > 0;
  const hasLatenessCredit = latenessStats.totalAvailableCredits > 0;

  // Determine current count for display
  // If credit is available but no progress yet, show bar as complete
  const cancellationCurrentCount =
    hasCancellationCredit && cancellationStats.completedShiftsTowardsNextCredit === 0
      ? cancellationStats.requiredShiftsForNextCredit
      : cancellationStats.completedShiftsTowardsNextCredit;

  const latenessCurrentCount =
    hasLatenessCredit && latenessStats.completedShiftsTowardsNextCredit === 0
      ? latenessStats.requiredShiftsForNextCredit
      : latenessStats.completedShiftsTowardsNextCredit;

  return (
    <PageWrapper
      sx={(theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        background: backgroundGradient ?? theme.palette.background.tertiary,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      })}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingX: 4,
          paddingTop: 10,
        }}
      >
        {/* Header Section */}
        <Stack spacing={4} alignItems="center" textAlign="center" sx={{ width: "100%" }}>
          <Text variant="h3">Your Emergency Credits</Text>

          <Text
            variant="h6"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontWeight: 500,
            })}
          >
            Working shifts earns progress towards credits that protect your Clipboard Score.
          </Text>
        </Stack>

        {/* Progress Card Section */}
        <Box
          sx={(theme) => ({
            marginTop: 12,
            backgroundColor: theme.palette.background.primary,
            borderRadius: "15px",
            paddingX: 6,
            paddingTop: 6,
            paddingBottom: 8,
            width: "100%",
          })}
        >
          <Stack spacing={5}>
            {/* Cancellation Credit Progress */}
            <CreditProgressBarWithTooltip
              completedCount={cancellationCurrentCount}
              requiredCount={cancellationStats.requiredShiftsForNextCredit}
              label={getCreditProgressBarLabel("cancellation", hasCancellationCredit)}
              color="blue"
              mode="fill"
              showTooltip={shouldShowTooltip(
                cancellationStats.completedShiftsTowardsNextCredit,
                cancellationStats.requiredShiftsForNextCredit,
                hasCancellationCredit
              )}
            />

            {/* Lateness Credit Progress */}
            <CreditProgressBarWithTooltip
              completedCount={latenessCurrentCount}
              requiredCount={latenessStats.requiredShiftsForNextCredit}
              label={getCreditProgressBarLabel("lateness", hasLatenessCredit)}
              color="green"
              mode="fill"
              showTooltip={shouldShowTooltip(
                latenessStats.completedShiftsTowardsNextCredit,
                latenessStats.requiredShiftsForNextCredit,
                hasLatenessCredit
              )}
            />
          </Stack>
        </Box>
      </Box>

      <DialogFooter orientation="horizontal">
        <Stack spacing={5} alignItems="center" sx={{ width: "100%" }}>
          <TextButton onClick={onLearnMoreClick}>What are Emergency Credits?</TextButton>

          <Button fullWidth variant="contained" size="large" onClick={onContinue}>
            Continue
          </Button>
        </Stack>
      </DialogFooter>
    </PageWrapper>
  );
}
